// src/components/Header/index.js - UNIFIED HEADER
import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getBaseConfig } from '../../config/restaurantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import StoreSelector from '../StoreSelector';
import LocationModal from '../LocationModal';
import UserProfileModal from '../UserProfileModal';
import ChangePasswordModal from '../ChangePasswordModal';
import apiCache from '../../utils/apiCache';
import { cartManager } from '../../utils/cartManager';
import useAddressManager from '../Useaddressmanager/useAddressManager';
import CartSelectionModal from './CartSelectionModal';
import StoreClosedModal from '../StoreClosedModal';
import { checkStoreAvailability } from '../checkStoreAvailability';
import { ShoppingBag, CreditCard, MapPin, Settings, LogOut, User, UserRoundCog, Menu, X, ShoppingCart, Lock, Download, UserX, Award, ChevronRight } from 'lucide-react';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

// Remembers which nav section was active for each history entry (keyed by
// React Router's per-entry location.key) — the same pattern App.js's
// ScrollToTop already uses to remember scroll position per key, reused
// here rather than building a separate mechanism. On Back/Forward, Header
// (which fully unmounts/remounts per route — it's rendered inside
// HomePage/PolicyPage, not persistently) resets activeSection to its
// default and only the scroll-spy's own geometry check can correct it.
// That check can run before the restored page has finished loading async
// content below the fold, landing on the wrong section (e.g. Gallery
// instead of Contact/Footer). Seeding from this map lets Back navigation
// show the right section immediately; normal scroll-spy still takes over
// from there for further scrolling.
//
// Stores { section, savedAt } rather than just the section: React
// StrictMode (enabled in index.js) intentionally mounts every component
// twice in development — mount, run effects, clean up, mount again — and
// on the very first-ever page load, useNavigationType() also reports
// 'POP' (matching "whatever the browser started on"), same as a genuine
// Back navigation. Without a time check, that phantom first mount writes
// an entry here, and the very next (real) mount then mistakes its own
// StrictMode remount for a genuine "returning after visiting Policy"
// restore, freezing the header before the user has scrolled anywhere.
// StrictMode's remount happens synchronously (same tick); an actual round
// trip to another page and back always takes far longer, so requiring a
// minimum age on the saved entry reliably tells them apart.
const activeSectionByKey = new Map();
const MIN_SAVED_AGE_MS = 500;

const Header = ({
    isFixed,
    logo,
    navLinks,
    restaurantName,
    basePath,
    onStoreSelect,
    userLocation,
    onLocationUpdate,
    onLocationChangeFromMenu,
    userToken,
    userId,
    userPhone,
    onLogout,
    onShowAuthModal,
    authMethod,
    // ✅ Add these from AppContent and sharedProps
    addresses: propAddresses,
    selectedAddress: propSelectedAddress,
    onAddressSelect,
    onSetDefaultAddress,
    addressesLoading
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('#banner');
    const [isLoading, setIsLoading] = useState(true);
    const [showStoreSelector, setShowStoreSelector] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showUserProfile, setShowUserProfile] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [locationDisplay, setLocationDisplay] = useState('Detect Location');
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    // Inline Profile Editing States
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [inlineImageFile, setInlineImageFile] = useState(null);
    const [inlineImagePreview, setInlineImagePreview] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();

    // Global Cart State
    const [activeCarts, setActiveCarts] = useState([]);
    const [totalCartItems, setTotalCartItems] = useState(0);
    const [showCartSelectionModal, setShowCartSelectionModal] = useState(false);
    const [showStoreClosedModal, setShowStoreClosedModal] = useState(false);
    const [closedStoreInfo, setClosedStoreInfo] = useState(null);

    // Determine login state
    const isLoggedIn = Boolean(userToken && userId);

    // Same conservative single-vs-multi-store default already used by
    // RestaurantMenuApp's own StoreClosedModal instance — no store count
    // known yet defaults to "assume multi-store".
    const closedModalCachedBranches = apiCache.getBranches();
    const closedModalHasOtherStores = !closedModalCachedBranches || closedModalCachedBranches.length !== 1;

    // Removed local useAddressManager - now using props for central sync

    // Update global carts
    const refreshCarts = useCallback(() => {
        const carts = cartManager.getActiveCarts();
        setActiveCarts(carts);
        setTotalCartItems(carts.reduce((acc, c) => acc + c.itemCount, 0));
    }, []);

    useEffect(() => {
        refreshCarts();

        // If branches are not in cache, fetch them to ensure carts have proper store names/details
        const cachedBranches = apiCache.getBranches();
        if (!cachedBranches || cachedBranches.length === 0) {
            const fetchBranchesForHeader = async () => {
                try {
                    const response = await axios.get(`${API_BASE_URL}/web/branch`, {
                        headers: {
                            'X-Api-Key': X_API_KEY,
                            'X-Tenant': getTenantId()
                        }
                    });
                    if (response.data?.data) {
                        apiCache.setBranches(response.data.data);
                        refreshCarts();
                    }
                } catch (err) {
                    console.error('Error fetching branches for header:', err);
                }
            };
            fetchBranchesForHeader();
        }

        window.addEventListener('cart_updated', refreshCarts);
        window.addEventListener('storage', refreshCarts);
        return () => {
            window.removeEventListener('cart_updated', refreshCarts);
            window.removeEventListener('storage', refreshCarts);
        };
    }, [refreshCarts]);

    const handleCartClick = useCallback(async () => {
        const isOnRestaurantMenu = location.pathname.startsWith('/restaurant/');
        const isOnHomePage = location.pathname === '/';

        // Case: User is inside RestaurantMenuApp
        if (isOnRestaurantMenu) {
            const sidebar = document.querySelector('.menu-app-cart-sidebar');
            const isSidebarVisible = sidebar && window.getComputedStyle(sidebar).display !== 'none';

            if (!isSidebarVisible) {
                // Mobile/Tablet: Open cart drawer
                window.dispatchEvent(new Event('open_cart'));
            } else {
                // Desktop: Sidebar is visible, scroll to it
                sidebar.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setIsOpen(false);
            return;
        }

        // Case: From Home page or any other page
        if (activeCarts.length === 0) {
            // No items in any cart → open store selector
            navigate('/restaurants');
        } else if (activeCarts.length > 1) {
            // Multiple carts → show selection modal
            setShowCartSelectionModal(true);
        } else {
            // Exactly one cart → navigate directly
            const cart = activeCarts[0];
            if (cart && cart.storeSlug) {
                const storeId = cart.branchDetails?.id;
                let branch = cart.branchDetails;

                // Always check live branch schedule so we don't rely on stale schedule stored in cart
                if (storeId) {
                    try {
                        const res = await axios.get(`${API_BASE_URL}/web/branch/${storeId}`, {
                            headers: {
                                'X-Api-Key': X_API_KEY,
                                'X-Tenant': getTenantId()
                            },
                            params: { _t: Date.now() }
                        });
                        if (res.data?.data) {
                            branch = { ...branch, ...res.data.data };
                            apiCache.setStoreDetails(storeId, res.data.data);
                        }
                    } catch (err) {
                        const cached = apiCache.getStoreDetails(storeId);
                        if (cached) branch = { ...branch, ...cached };
                    }
                }

                const availability = branch?.week_schedule
                    ? checkStoreAvailability(branch.week_schedule)
                    : { isOpen: true, message: '', nextOpenTime: null };
                const supportsPreOrder = branch?.allow_pre_order === true || branch?.pre_order_enabled === true;

                if (!availability.isOpen && !supportsPreOrder) {
                    setClosedStoreInfo({
                        store: {
                            ...branch,
                            location: branch.location || `${branch.city}, ${branch.state}`
                        },
                        message: availability.message,
                        nextOpenTime: availability.nextOpenTime
                    });
                    setShowStoreClosedModal(true);
                    setIsOpen(false);
                    return;
                }

                navigate(`/restaurant/${cart.storeSlug}`, {
                    state: {
                        orderDetails: {
                            type: cart.orderType,
                            store: branch
                        }
                    }
                });
            } else {
                navigate('/restaurants');
            }
        }
        setIsOpen(false);
    }, [activeCarts, navigate, location.pathname]);

    const handleSelectCart = useCallback((cart) => {
        setShowCartSelectionModal(false);
        const currentStoreSlug = location.pathname.split('/restaurant/')[1];
        if (currentStoreSlug && currentStoreSlug === cart.storeSlug) {
            window.dispatchEvent(new Event('open_cart'));
            setIsOpen(false);
        } else {
            navigate(`/restaurant/${cart.storeSlug}`, {
                state: {
                    orderDetails: {
                        type: cart.orderType,
                        store: cart.branchDetails
                    }
                }
            });
            setIsOpen(false);
        }
    }, [navigate, location.pathname]);

    // Auto-hide Store Closed modal in Header as soon as merchant reopens store in backend
    useEffect(() => {
        if (!showStoreClosedModal || !closedStoreInfo?.store?.id) return;
        const storeId = closedStoreInfo.store.id;

        const checkLiveReopen = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/web/branch/${storeId}`, {
                    headers: {
                        'X-Api-Key': X_API_KEY,
                        'X-Tenant': getTenantId()
                    },
                    params: { _t: Date.now() }
                });
                const freshBranch = res.data?.data;
                if (freshBranch?.week_schedule) {
                    apiCache.setStoreDetails(storeId, freshBranch);
                    const availability = checkStoreAvailability(freshBranch.week_schedule);
                    if (availability.isOpen) {
                        console.log('🟢 Store reopened! Auto-hiding Store Closed modal in Header.');
                        setShowStoreClosedModal(false);
                        setClosedStoreInfo(null);
                    }
                }
            } catch (err) {
                // ignore error
            }
        };

        const intervalId = setInterval(checkLiveReopen, 5000);
        const onFocus = () => {
            if (document.visibilityState === 'visible') checkLiveReopen();
        };

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [showStoreClosedModal, closedStoreInfo]);


    // Fetch profile data for dropdown/sidebar display
    const fetchProfileData = useCallback(async (force = false) => {
        // 1. CHECK CACHED PROFILE FIRST (unless forced)
        const cachedProfile = apiCache.getUserProfile();
        if (cachedProfile && !force) {
            console.log('⚡ Header using cached profile data');
            setProfileData(cachedProfile);
            return;
        }

        const token = getValidAuthSession()?.token;
        if (!token) return;
        setProfileLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/web/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Api-Key': X_API_KEY,
                    'X-Tenant': getTenantId(),
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            if (response.data && response.data.data) {
                // 2. STORE IN CACHE
                apiCache.setUserProfile(response.data.data);
                setProfileData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching profile for header:', err);
        } finally {
            setProfileLoading(false);
        }
    }, []);

    // Fetch profile when user is logged in
    useEffect(() => {
        if (isLoggedIn && !profileData) {
            fetchProfileData();
        }
    }, [isLoggedIn, profileData, fetchProfileData]);

    // Listen for custom profile update events
    useEffect(() => {
        const handleProfileUpdate = () => {
            console.log('🔄 Ext. profile update detected, fetching new profile data for Header...');
            fetchProfileData(true);
        };

        window.addEventListener('profile_updated', handleProfileUpdate);
        return () => window.removeEventListener('profile_updated', handleProfileUpdate);
    }, [fetchProfileData]);

    // Clear profile data on logout
    useEffect(() => {
        if (!isLoggedIn) {
            setProfileData(null);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const syncLocation = () => {
            const savedDisplay = localStorage.getItem('location_display');
            if (savedDisplay && savedDisplay !== 'Select Location') {
                setLocationDisplay(savedDisplay);
                return;
            }

            const savedLocation = localStorage.getItem('user_location');
            if (savedLocation) {
                try {
                    const parsed = JSON.parse(savedLocation);
                    if (parsed.latitude && parsed.longitude) {
                        fetchLocationName(parsed.latitude, parsed.longitude);
                        return;
                    }
                } catch (e) {
                    console.error('Error parsing saved location:', e);
                }
            }

            const locationStatus = localStorage.getItem('location_requested');
            if (locationStatus === 'denied' || locationStatus === 'error') {
                setLocationDisplay('Select Location');
            }
        };

        // If we have a cached display string, use it instantly. Avoid reverse geocoding
        // every single page load which hits rate limits and overrides custom labels like "Home".
        const savedDisplay = localStorage.getItem('location_display');
        if (savedDisplay && savedDisplay !== 'Select Location') {
            setLocationDisplay(savedDisplay);
        } else if (userLocation && userLocation.latitude && userLocation.longitude) {
            fetchLocationName(userLocation.latitude, userLocation.longitude);
        } else {
            syncLocation();
        }

        window.addEventListener('locationUpdated', syncLocation);
        window.addEventListener('storage', syncLocation);

        return () => {
            window.removeEventListener('locationUpdated', syncLocation);
            window.removeEventListener('storage', syncLocation);
        };
    }, [userLocation]);

    const fetchLocationName = async (lat, lng) => {
        try {
            console.log('📍 Fetching location name for:', lat, lng); // ✅ ADD THIS

            // Using Photon for better CORS support
            const response = await fetch(
                `https://photon.komoot.io/reverse?lon=${lng}&lat=${lat}`
            );

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Location data:', data); // ✅ ADD THIS

                if (data.features && data.features.length > 0) {
                    const props = data.features[0].properties;

                    // ✅ IMPROVED: Prioritize specific local names (Area/Street/Suburb) over generic City/District
                    const specificName = props.name || props.street || props.suburb || props.district || props.neighbourhood;
                    const city = props.city || props.town || props.village || props.county;
                    const state = props.state;

                    let locationParts = [];

                    if (specificName) locationParts.push(specificName);
                    if (city && city !== specificName) locationParts.push(city);
                    // Add state only if we don't have a specific name (to keep it short)
                    if (!specificName && state) locationParts.push(state);

                    let locationStr = locationParts.join(', ');

                    // Fallback to state if nothing else
                    if (!locationStr && state) locationStr = state;
                    if (!locationStr) locationStr = 'Location detected';

                    console.log('📌 Setting location display:', locationStr); // ✅ ADD THIS

                    setLocationDisplay(locationStr);
                    localStorage.setItem('location_display', locationStr);

                    // ✅ NEW: Capture and store pincode as fallback for cart analysis
                    if (props.postcode) {
                        console.log('📬 Detected pincode:', props.postcode);
                        localStorage.setItem('detected_pincode', props.postcode);
                    }
                } else {
                    console.error('❌ Location fetch failed (empty features)');
                    if (!localStorage.getItem('location_display') || localStorage.getItem('location_display') === 'Select Location') {
                        setLocationDisplay('Select Location');
                    }
                }
            } else {
                console.error('❌ Location fetch failed (non-ok response)');
                if (!localStorage.getItem('location_display') || localStorage.getItem('location_display') === 'Select Location') {
                    setLocationDisplay('Select Location');
                }
            }
        } catch (error) {
            console.error('❌ Error fetching location name:', error);
            if (!localStorage.getItem('location_display') || localStorage.getItem('location_display') === 'Select Location') {
                setLocationDisplay('Select Location');
            }
        }
    };

    useEffect(() => {
        const handleLoad = () => {
            setIsLoading(false);
        };

        // Safety fallback: ensure loader disappears after 1.5 seconds regardless
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500);

        if (document.readyState === 'complete') {
            setIsLoading(false);
        } else {
            window.addEventListener('load', handleLoad);
        }

        return () => {
            window.removeEventListener('load', handleLoad);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const sections = navLinks.map(item => item.href.substring(1));
            const THRESHOLD = 150;
            let currentSection = '#banner';
            let matched = false;

            // A section is "current" when its box actually straddles the
            // reference line (top has reached it, bottom hasn't passed it
            // yet) — checking only rect.top <= THRESHOLD, as this used to,
            // is also trivially true for every section already scrolled
            // fully past (their top is just some very negative number), so
            // whichever one happens to be the nearest such section on a
            // pixel-threshold miss (e.g. the true current section's top
            // lands at 151px instead of 150 — exactly what happens right
            // after Back-navigation scroll restoration, while the restored
            // page is still growing from async content loading in below
            // it) would silently win instead. Checking the full span fixes
            // that without depending on the scroll-restoration timing at
            // all — it just asks "which section is on screen right now".
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = document.getElementById(sections[i]);
                if (section) {
                    const rect = section.getBoundingClientRect();
                    if (rect.top <= THRESHOLD && rect.bottom > THRESHOLD) {
                        currentSection = `#${sections[i]}`;
                        matched = true;
                        break;
                    }
                }
            }

            // Fallback for the bottom of the page: a short last section (or
            // one whose top can't reach THRESHOLD because the document
            // simply ends first) may never straddle the line even when
            // it's the only thing on screen — treat "scrolled to the
            // bottom" as that last section being active.
            if (!matched) {
                const scrollY = Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);
                const maxScrollY = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
                if (scrollY >= maxScrollY - 2) {
                    for (let i = sections.length - 1; i >= 0; i--) {
                        if (document.getElementById(sections[i])) {
                            currentSection = `#${sections[i]}`;
                            break;
                        }
                    }
                }
            }

            setActiveSection(currentSection);
            activeSectionByKey.set(location.key, { section: currentSection, savedAt: Date.now() });
        };

        // capture:true on document, not window: this app's page scrolls via
        // <body> (App.css/index.css: "html, body { height: 100% }" plus
        // body's own overflow-y: auto), not the window/documentElement, and
        // scroll events don't bubble — a window-level listener never fires
        // for it, so this only ever ran once at mount and never updated
        // again as the user scrolled. That's why the highlighted nav item
        // could go stale (e.g. stuck on Gallery) after scroll-position
        // restoration on Back navigation: the one-time initial read just
        // didn't reflect the actually-restored position. Listening on
        // document with capture:true catches scroll events from any
        // scrollable descendant, including body, so this now stays in sync
        // with the real scroll position continuously — including scrolling
        // done by the Back-navigation scroll restoration itself.
        //
        // On Back/Forward specifically, that last point is also the source
        // of a *flicker*: App.js's ScrollToTop keeps the restored scroll
        // position pinned while Home's async content is still arriving in
        // two waves (confirmed by direct tracing), and every one of its
        // re-assertions fires a genuine 'scroll' event here. While that's
        // happening, the *same* fixed scrollY sits under *changing*
        // content, so this same geometry check can genuinely compute a
        // different (transiently wrong, e.g. Gallery) section from one
        // event to the next before settling back on the right one (e.g.
        // Contact) — that's the zig-zag. It isn't a bug in the check
        // itself; the page really is unstable for that window. So rather
        // than debounce (which would still let some of those transient
        // reads through whenever there's a lull between the two loading
        // waves) or fake/delay the visual result, this suppresses
        // committing the live geometry result entirely for that known
        // settling window, trusting the seeded value (below) as the single
        // source of truth until the page is guaranteed stable — matching
        // ScrollToTop's own 8s safety window, since that's what bounds how
        // long its re-assertions can still be running. A real scroll or
        // touch from the user lifts the suppression immediately, since
        // that's deliberate input and should always win.
        const savedEntry = activeSectionByKey.get(location.key);
        const isSettling = navigationType === 'POP' && !!savedEntry && (Date.now() - savedEntry.savedAt > MIN_SAVED_AGE_MS);
        let suppressed = isSettling;
        let settleTimer = null;
        let lift = () => { };

        if (isSettling) {
            settleTimer = setTimeout(() => { suppressed = false; }, 8000);
            lift = () => { suppressed = false; clearTimeout(settleTimer); };
            window.addEventListener('wheel', lift, { passive: true, once: true });
            window.addEventListener('touchmove', lift, { passive: true, once: true });
        }

        const onScrollEvent = () => {
            if (!suppressed) handleScroll();
        };

        document.addEventListener('scroll', onScrollEvent, { capture: true, passive: true });

        // For a fresh visit or a normal link click (nothing seeded), there's
        // no settling window and no seeded value to protect — run exactly
        // as this always did.
        if (!isSettling) {
            handleScroll();
        }

        return () => {
            clearTimeout(settleTimer);
            document.removeEventListener('scroll', onScrollEvent, { capture: true });
            window.removeEventListener('wheel', lift);
            window.removeEventListener('touchmove', lift);
        };
    }, [navLinks, location.pathname, location.key, navigationType]);

    // Seed activeSection synchronously (before paint) from the last known
    // section for this history entry on Back/Forward — see
    // activeSectionByKey above for why this is needed (and why a minimum
    // age is required) rather than relying solely on the scroll-spy's own
    // geometry check.
    useLayoutEffect(() => {
        const savedEntry = activeSectionByKey.get(location.key);
        if (navigationType === 'POP' && savedEntry && (Date.now() - savedEntry.savedAt > MIN_SAVED_AGE_MS)) {
            setActiveSection(savedEntry.section);
        }
    }, [location.key, navigationType]);

    useEffect(() => {
        if (showStoreSelector || showLocationModal || showUserProfile) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showStoreSelector, showLocationModal, showUserProfile]);

    // Close profile dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
                setProfileDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [profileDropdownOpen]);

    // ✅ Reset mobile-only menu/account state once it no longer applies.
    // The mobile drawer's "My Account" <li>s (divider, user-info card,
    // inline edit form, My Orders/My Transactions/etc.) are children of
    // the same <ul class="nav ul-nav"> the desktop nav links use, gated
    // only by isOpen — there's no CSS breakpoint hiding them. isOpen/
    // isEditingProfile/profileDropdownOpen are otherwise only ever closed
    // by explicit clicks (hamburger toggle, nav-link clicks, etc.), so a
    // DevTools viewport resize back to desktop, or browser Back/Forward
    // (which fires no click handler), can leave them stuck true — and
    // those <li>s then render inline in the desktop flex-row nav bar.
    // 992px matches the drawer's own `@media (max-width: 992px)` in
    // Header.css.
    useEffect(() => {
        const MOBILE_BREAKPOINT = 992;
        const handleResize = () => {
            if (window.innerWidth > MOBILE_BREAKPOINT) {
                setIsOpen(false);
                setIsEditingProfile(false);
                setProfileDropdownOpen(false);
                document.body.classList.remove('mobile-menu-open');
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Same reset on every route change (covers browser Back/Forward, which
    // bypasses the nav-link onClick handlers that would otherwise close
    // the menu).
    useEffect(() => {
        setIsOpen(false);
        setIsEditingProfile(false);
        setProfileDropdownOpen(false);
        document.body.classList.remove('mobile-menu-open');
    }, [location.pathname]);

    const handleCloseMenu = useCallback(() => {
        setIsOpen(false);
        setIsEditingProfile(false);
        document.body.classList.remove('mobile-menu-open');
    }, []);

    const toggleMenu = () => {
        const nextState = !isOpen;
        setIsOpen(nextState);
        if (!nextState) setIsEditingProfile(false);
        if (nextState) {
            document.body.classList.add('mobile-menu-open');
        } else {
            document.body.classList.remove('mobile-menu-open');
        }
    };

    // Inline Profile Editing Handlers
    const handleStartEditProfile = (e) => {
        e.stopPropagation();
        setEditName(profileData?.name || '');
        setEditPhone(profileData?.phone || '');
        setEditEmail(profileData?.email || '');
        setIsEditingProfile(true);
        setProfileError(null);
        setInlineImageFile(null);
        setInlineImagePreview(null);
    };

    const handleCancelEditProfile = (e) => {
        if (e) e.stopPropagation();
        setIsEditingProfile(false);
        setProfileError(null);
    };

    const handleSaveProfileInline = async (e) => {
        if (e) e.stopPropagation();
        const token = getValidAuthSession()?.token;
        if (!token) return;

        if (!editName.trim()) {
            setProfileError('Name is required');
            return;
        }

        setProfileSaving(true);
        setProfileError(null);

        try {
            let config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Api-Key': X_API_KEY,
                    'X-Tenant': getTenantId(),
                    'Accept': 'application/json'
                }
            };

            let payload;
            let endpoint = `${API_BASE_URL}/web/me`;

            if (inlineImageFile) {
                payload = new FormData();
                payload.append('name', editName.trim());
                payload.append('phone', editPhone.trim());
                payload.append('email', editEmail.trim());
                payload.append('image', inlineImageFile);
                config.headers['Content-Type'] = 'multipart/form-data';
            } else {
                payload = {
                    name: editName.trim(),
                    phone: editPhone.trim(),
                    email: editEmail.trim()
                };
                config.headers['Content-Type'] = 'application/json';
            }

            const response = await axios.put(endpoint, payload, config);

            if (response.data && response.data.data) {
                setProfileData(response.data.data);
                setIsEditingProfile(false);
            }
        } catch (err) {
            console.error('Error saving profile inline:', err);
            setProfileError(err.response?.data?.message || 'Failed to save changes');
        } finally {
            setProfileSaving(false);
        }
    };

    const handleInlineImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setProfileError('Image must be less than 5MB');
                return;
            }
            setInlineImageFile(file);
            setInlineImagePreview(URL.createObjectURL(file));
        }
    };

    const handleStoreSelect = (store) => {
        console.log('🏪 Store selected from Header:', store.name);
        setShowStoreSelector(false);

        if (onStoreSelect) {
            onStoreSelect(store);
        }

        // Navigate to restaurants page with selected store
        setTimeout(() => {
            navigate('/restaurants', {
                state: {
                    selectedStore: store,
                    fromHeader: true
                }
            });
        }, 100);
    };

    const handleLocationClick = () => {
        console.log('📍 Location clicked in Header');
        setShowLocationModal(true);
    };

    const handleLocationUpdate = (newLocation) => {
        console.log('📍 Location updated in Header:', newLocation);

        if (newLocation.latitude && newLocation.longitude) {
            fetchLocationName(newLocation.latitude, newLocation.longitude);
            if (onLocationUpdate) {
                onLocationUpdate(newLocation);
            }
        } else if (newLocation.display) {
            setLocationDisplay(newLocation.display);
            localStorage.setItem('location_display', newLocation.display);
        }

        setShowLocationModal(false);

        if (onLocationChangeFromMenu && (location.pathname === '/restaurants' || location.pathname.startsWith('/restaurant/'))) {
            console.log('🔄 Location changed in menu - calling onLocationChangeFromMenu');
            onLocationChangeFromMenu();
        }
    };

    const handleAddressSelectFromLocation = async (address) => {
        console.log('📍 Address selected from LocationModal:', address.label);

        // 1. Mark as default in the backend
        if (onSetDefaultAddress) {
            try {
                await onSetDefaultAddress(address.id);
            } catch (err) {
                console.error('Failed to set global default address:', err);
            }
        }

        // 2. Select centrally via parent's hook
        onAddressSelect(address);

        // 3. Dispatch global sync event for any legacy components
        window.dispatchEvent(new Event('global_address_sync'));

        // Update location display in header
        const displayText = address.label || address.address;
        setLocationDisplay(displayText);
        localStorage.setItem('location_display', displayText);

        // Update user location coords
        if (address.latitude && address.longitude) {
            const locationData = {
                latitude: parseFloat(address.latitude),
                longitude: parseFloat(address.longitude),
                timestamp: new Date().toISOString()
            };
            localStorage.setItem('user_location', JSON.stringify(locationData));
            if (onLocationUpdate) {
                onLocationUpdate(locationData);
            }
        }
    };

    const handleOpenAddressForm = () => {
        console.log('📍 Opening address form from LocationModal');
        setShowLocationModal(false);
        navigate('/addresses');
    };

    // ✅ Handle scroll-to-section after navigating to home from another page
    // Using useLayoutEffect instead of useEffect so the scroll happens BEFORE the browser paints.
    // This prevents the visual "jump" from the top of the page down to the section.
    useLayoutEffect(() => {
        const isHomePath = location.pathname === '/' || location.pathname === basePath;

        if (isHomePath && location.hash) {
            const target = location.hash;

            // Wait for section to render, then apply smooth scroll
            const scrollWithRetry = (sel, retries = 20, delay = 200) => {
                const el = document.querySelector(sel);

                // If element exists and has height
                if (el && el.offsetHeight > 50) {

                    // We trigger the scroll at 3 stages to outsmart image layout shifts:
                    // For cross-page navigation, we use 'auto' (instant jump) so the user 
                    // doesn't see the page scroll all the way down from the top.

                    // 1. Instantly (snaps to correct initial view before paint)
                    el.scrollIntoView({ behavior: 'auto', block: 'start' });
                    setActiveSection(sel);

                    // 2. After 500ms (adjusts instantly if some text/DOM shifted)
                    setTimeout(() => {
                        const currentEl = document.querySelector(sel);
                        if (currentEl) currentEl.scrollIntoView({ behavior: 'auto', block: 'start' });
                    }, 500);

                    // 3. After 1500ms (final adjustment for delayed images loading)
                    setTimeout(() => {
                        const currentEl = document.querySelector(sel);
                        if (currentEl) currentEl.scrollIntoView({ behavior: 'auto', block: 'start' });
                    }, 1500);

                    return;
                }

                // If not found or zero height, keep retrying
                if (retries > 0) {
                    setTimeout(() => scrollWithRetry(sel, retries - 1, delay), delay);
                }
            };

            scrollWithRetry(target);
        }
    }, [location.pathname, location.hash]);

    // ✅ COMPLETELY FIXED: Handle nav link clicks
    const handleNavLinkClick = (e, href) => {
        // Only prevent default if it's a hash link, allowing real URLs to behave normally
        if (href.startsWith('#')) {
            e.preventDefault();
        }

        console.log('🔗 Nav link clicked:', href);
        console.log('📍 Current location:', location.pathname);

        setIsOpen(false);
        document.body.classList.remove('mobile-menu-open');

        // ✅ FIX: Handle ORDER NOW button properly
        if (href === '/order' || href === '/restaurants') {
            e.preventDefault();
            console.log('🛒 ORDER NOW clicked');

            // If already on restaurants page, ignore the click
            if (location.pathname === '/restaurants') {
                return;
            }

            // Navigate to store selection
            navigate('/restaurants');
            return;
        }

        // Handle navigation to other sections (home page sections starting with #)
        if (href.startsWith('#')) {
            const isHomePath = location.pathname === '/' || location.pathname === basePath;

            if (!isHomePath) {
                // NOT on the home page — navigate to home with the hash
                const homePath = basePath || '/';
                navigate(`${homePath}${href}`);
            } else {
                // Already on the home page — update URL and scroll
                const newPath = `${location.pathname}${href}`;
                window.history.pushState(null, '', newPath);

                const el = document.querySelector(href);
                if (el) {
                    el.scrollIntoView({ behavior: 'auto', block: 'start' });
                    setActiveSection(href);
                }
            }
        } else {
            // It's a regular path link (e.g. /about), navigate directly
            navigate(href);
        }
    };

    const handleLoginClick = () => {
        if (!isLoggedIn) {
            if (onShowAuthModal) {
                onShowAuthModal();
            }
        } else {
            setShowUserProfile(true);
        }
        setIsOpen(false);
        setProfileDropdownOpen(false);
        document.body.classList.remove('mobile-menu-open');
    };

    const handleSectionClick = (section) => {
        setProfileDropdownOpen(false);
        setIsOpen(false);
        document.body.classList.remove('mobile-menu-open');
        navigate(`/${section}`);
    };

    const handleLoginFromProfile = () => {
        if (onShowAuthModal) {
            onShowAuthModal();
        }
    };

    const handleLogoutClick = () => {
        if (onLogout) {
            onLogout();
        }
        setProfileDropdownOpen(false);
        setIsOpen(false);
        document.body.classList.remove('mobile-menu-open');
        navigate('/');
    };

    // GDPR: download the customer's own data as a JSON file
    const handleDownloadMyData = async () => {
        const token = getValidAuthSession()?.token;
        if (!token) return;

        try {
            // format=xlsx: a multi-sheet Excel workbook is far more usable for a
            // non-technical customer than raw JSON — the backend still supports
            // plain JSON (the default) for any other caller.
            const res = await axios.get(`${API_BASE_URL}/web/me/data-export`, {
                params: { format: 'xlsx' },
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Api-Key': X_API_KEY,
                    'X-Tenant': getTenantId(),
                }
            });

            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'my-data.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            // responseType 'blob' means error bodies arrive as a Blob too (even
            // though the backend sent JSON) — read it back out to get the message.
            let message = 'Could not download your data. Please try again.';
            if (err.response?.data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await err.response.data.text());
                    message = parsed.message || message;
                } catch {
                    // Non-JSON error body — keep the generic message.
                }
            } else {
                message = err.response?.data?.message || message;
            }
            alert(message);
        } finally {
            setProfileDropdownOpen(false);
            setIsOpen(false);
            // Every other place that closes the mobile menu also removes
            // this body class (see the nav-link handlers above) — it drives
            // body.mobile-menu-open .hamburger-menu-btn { display:none }
            // and the scroll-lock (position:fixed etc.) in Header/index.css.
            // This handler was missing it, so after a download the React
            // isOpen state correctly flipped to false and the drawer
            // visually closed, but the body class stayed stuck, leaving the
            // hamburger permanently display:none and the page scroll-locked.
            document.body.classList.remove('mobile-menu-open');
        }
    };

    // GDPR: request account/data deletion — logged for the business to action
    // manually, nothing is deleted automatically (see backend for why).
    const handleRequestAccountDeletion = async () => {
        const token = getValidAuthSession()?.token;
        if (!token) return;

        const confirmed = window.confirm(
            'Request deletion of your account and data? The business will review and process this request; it is not immediate.'
        );
        if (!confirmed) return;

        try {
            const res = await axios.post(`${API_BASE_URL}/web/me/erasure-request`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Api-Key': X_API_KEY,
                    'X-Tenant': getTenantId(),
                    'Accept': 'application/json'
                }
            });
            alert(res.data.message || 'Your deletion request has been received.');
        } catch (err) {
            alert(err.response?.data?.message || 'Could not submit your request. Please try again.');
        } finally {
            setProfileDropdownOpen(false);
            setIsOpen(false);
            // Same missing cleanup as handleDownloadMyData above — without
            // this, body.mobile-menu-open stays stuck and the hamburger
            // stays display:none after this flow completes.
            document.body.classList.remove('mobile-menu-open');
        }
    };

    const handleRequestLocationFromStore = () => {
        console.log('📍 Location change requested from StoreSelector in Header');
        setShowLocationModal(true);
    };

    const collapseClass = `navbar-collapse ${isOpen ? 'in' : ''}`;
    const headerClass = `header-block-top ${isFixed ? 'fixed-menu' : ''}`;

    const allNavLinks = [...navLinks];

    // ----- Default Header Layout -----
    return (
        <>
            {isLoading && (
                <div id="loader">
                    <div id="status"></div>
                </div>
            )}

            <div id="site-header">
                <header id="header" className={headerClass}>
                    <div className="container">
                        <div className="main-menu">
                            <nav className="navbar" id="mainNav">

                                {/* ── Left: Logo + Location ── */}
                                <div className="navbar-header">
                                    <div className="logo">
                                        <a
                                            className="navbar-brand logo-header"
                                            href={basePath || '/'}
                                            onClick={(e) => { e.preventDefault(); navigate(basePath || '/'); }}
                                        >
                                            <img src={logo} alt={restaurantName} />
                                        </a>
                                        <button
                                            className="location-display"
                                            onClick={handleLocationClick}
                                            type="button"
                                        >
                                            <i className="fa fa-map-marker" aria-hidden="true"></i>
                                            <span>{locationDisplay}</span>
                                            <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                        </button>
                                    </div>

                                    {/* Mobile controls */}
                                    <div className="mobile-header-controls">
                                        <button
                                            className="global-cart-btn mobile-only-cart"
                                            onClick={handleCartClick}
                                            title="Resume Order"
                                        >
                                            <ShoppingCart size={22} color="var(--header-text, #fff)" />
                                            {totalCartItems > 0 && (
                                                <span className="global-cart-badge">{totalCartItems}</span>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="hamburger-menu-btn"
                                            aria-expanded={isOpen}
                                            aria-label="Toggle navigation"
                                            onClick={toggleMenu}
                                        >
                                            <i className="fa fa-bars" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* ── Right: Nav + Drawer ── */}
                                <div id="navbar" className={collapseClass}>
                                    {/* Drawer header (mobile only) */}
                                    <div className="drawer-header">
                                        <img src={logo} alt={restaurantName} className="drawer-logo" />
                                        <button
                                            type="button"
                                            className="close-menu-btn"
                                            onClick={toggleMenu}
                                            aria-label="Close menu"
                                        >
                                            <i className="fa fa-times" aria-hidden="true"></i>
                                        </button>
                                    </div>

                                    <ul className="nav ul-nav">
                                        {allNavLinks.map((item) => {
                                            const isActive = item.isOrderButton
                                                ? (location.pathname === '/restaurants' || location.pathname.startsWith('/restaurant/'))
                                                : location.pathname === '/' && activeSection === item.href;

                                            return (
                                                <li
                                                    key={item.href}
                                                    className={[
                                                        item.isOrderButton ? 'order-nav-item' : '',
                                                        isActive ? 'active' : ''
                                                    ].filter(Boolean).join(' ')}
                                                >
                                                    <a
                                                        href={item.href}
                                                        onClick={(e) => handleNavLinkClick(e, item.href)}
                                                        className={item.isOrderButton ? 'order-now-link' : ''}
                                                    >
                                                        {item.isOrderButton && (
                                                            <svg
                                                                width="16" height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.2"
                                                                style={{ verticalAlign: 'middle' }}
                                                            >
                                                                <circle cx="9" cy="21" r="1" />
                                                                <circle cx="20" cy="21" r="1" />
                                                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                                            </svg>
                                                        )}
                                                        {item.name}
                                                    </a>
                                                </li>
                                            );
                                        })}

                                        {/* Global Cart Icon inside Desktop Nav item */}
                                        <li className="nav-item cart-nav-item">
                                            <div className="global-cart-container" style={{ margin: '0 10px', height: '100%', display: 'flex' }}>
                                                <button
                                                    className="global-cart-btn"
                                                    onClick={handleCartClick}
                                                    title="Resume Order"
                                                >
                                                    <ShoppingCart size={22} />
                                                    {totalCartItems > 0 && (
                                                        <span className="global-cart-badge">{totalCartItems}</span>
                                                    )}
                                                </button>
                                            </div>
                                        </li>

                                        {/* Desktop Profile nav item */}
                                        <li className={`profile-nav-item ${profileDropdownOpen ? 'active' : ''}`}>
                                            {isLoggedIn ? (
                                                <div className="profile-dropdown-container">
                                                    <a
                                                        href="#profile"
                                                        className="profile-nav-link"
                                                        onClick={(e) => { e.preventDefault(); setProfileDropdownOpen(!profileDropdownOpen); }}
                                                    >
                                                        <div className="profile-indicator">
                                                            <User size={18} className="profile-icon" />
                                                            <span className="profile-name-text">{profileData?.name?.split(' ')[0] || ''}</span>
                                                            <i className={`fa fa-chevron-down profile-chevron ${profileDropdownOpen ? 'open' : ''}`}></i>
                                                        </div>
                                                    </a>

                                                    {profileDropdownOpen && (
                                                        <div className="profile-dropdown-menu">
                                                            {/* User profile card */}
                                                            <div className={`dropdown-profile-card ${isEditingProfile ? 'is-editing' : ''}`}>
                                                                <div className="dropdown-user-avatar">
                                                                    {isEditingProfile ? (
                                                                        <div className="inline-avatar-upload">
                                                                            <img src={inlineImagePreview || profileData?.image} alt="Profile" />
                                                                            <input type="file" accept="image/*" onChange={handleInlineImageChange} />
                                                                            <div className="upload-overlay"><i className="fa fa-camera"></i></div>
                                                                        </div>
                                                                    ) : (
                                                                        profileData && profileData.image && !profileData.image.includes('default/profile') ? (
                                                                            <img src={profileData.image} alt="Profile" />
                                                                        ) : (
                                                                            <User size={22} />
                                                                        )
                                                                    )}
                                                                </div>

                                                                {isEditingProfile ? (
                                                                    <div className="dropdown-profile-edit-form">
                                                                        <input
                                                                            type="text"
                                                                            value={editName}
                                                                            onChange={(e) => setEditName(e.target.value)}
                                                                            placeholder="Your Name"
                                                                            className="inline-edit-input"
                                                                        />
                                                                        <input
                                                                            type="tel"
                                                                            value={editPhone}
                                                                            onChange={(e) => setEditPhone(e.target.value)}
                                                                            placeholder="Phone Number"
                                                                            className="inline-edit-input"
                                                                        />
                                                                        <input
                                                                            type="email"
                                                                            value={editEmail}
                                                                            onChange={(e) => setEditEmail(e.target.value)}
                                                                            placeholder="Email Address"
                                                                            className="inline-edit-input"
                                                                        />
                                                                        {profileError && <span className="inline-error">{profileError}</span>}
                                                                        <div className="inline-edit-actions">
                                                                            <button className="inline-save-btn" onClick={handleSaveProfileInline} disabled={profileSaving}>
                                                                                {profileSaving ? '...' : <i className="fa fa-check"></i>}
                                                                            </button>
                                                                            <button className="inline-cancel-btn" onClick={handleCancelEditProfile} disabled={profileSaving}>
                                                                                <i className="fa fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div className="dropdown-profile-details">
                                                                            <span className="dropdown-profile-label">WELCOME</span>
                                                                            <span className="dropdown-profile-value">{profileData?.name || 'User'}</span>
                                                                        </div>
                                                                        <button className="dropdown-edit-btn" onClick={handleStartEditProfile} aria-label="Edit profile">
                                                                            <UserRoundCog size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {/* ✅ Loyalty strip — only renders when the API actually
                                                                returns a loyalty object, so tenants without loyalty
                                                                enabled see no change at all. Reuses profileData
                                                                Header already fetches from /web/me (no new call). */}
                                                            {profileData?.loyalty && (
                                                                <button className="dropdown-loyalty-strip" onClick={() => handleSectionClick('loyalty')}>
                                                                    <div className="dropdown-loyalty-top">
                                                                        <div className="dropdown-loyalty-info">
                                                                            <span className="dropdown-loyalty-badge">
                                                                                <Award size={14} /> {profileData.loyalty.tier?.display || 'Bronze'}
                                                                            </span>
                                                                            <span className="dropdown-loyalty-points">
                                                                                {profileData.loyalty.points?.available ?? 0} pts
                                                                            </span>
                                                                        </div>
                                                                        <ChevronRight size={16} className="dropdown-loyalty-arrow" />
                                                                    </div>
                                                                    {typeof profileData.loyalty.tier?.progress?.progress_percentage === 'number' && (
                                                                        <div className="dropdown-loyalty-progress-track">
                                                                            <div
                                                                                className="dropdown-loyalty-progress-fill"
                                                                                style={{ width: `${Math.min(100, Math.max(0, profileData.loyalty.tier.progress.progress_percentage))}%` }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </button>
                                                            )}

                                                            <div className="dropdown-divider"></div>
                                                            <button onClick={() => handleSectionClick('orders')}>
                                                                <ShoppingBag size={16} /> My Orders
                                                            </button>
                                                            <button onClick={() => handleSectionClick('transactions')}>
                                                                <CreditCard size={16} /> My Transactions
                                                            </button>
                                                            <button onClick={() => handleSectionClick('addresses')}>
                                                                <MapPin size={16} />  Addresses
                                                            </button>
                                                            {authMethod === 'email_password' && (
                                                                <button onClick={() => { setProfileDropdownOpen(false); setShowChangePasswordModal(true); }}>
                                                                    <Lock size={16} /> Change Password
                                                                </button>
                                                            )}
                                                            <button onClick={() => handleSectionClick('preferences')} style={{ display: 'none' }}>
                                                                <Settings size={16} /> Preferences
                                                            </button>
                                                            <div className="dropdown-divider"></div>
                                                            <button onClick={handleDownloadMyData}>
                                                                <Download size={16} /> Download My Data
                                                            </button>
                                                            <button onClick={handleRequestAccountDeletion}>
                                                                <UserX size={16} /> Delete My Account
                                                            </button>
                                                            <div className="dropdown-divider"></div>
                                                            <button onClick={handleLogoutClick} className="logout-btn">
                                                                <LogOut size={16} /> Logout
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <a
                                                    href="#login"
                                                    className="profile-nav-link login-link-with-icon"
                                                    onClick={(e) => { e.preventDefault(); handleLoginClick(); }}
                                                >
                                                    <User size={18} className="login-icon" />
                                                    <span>LOGIN</span>
                                                </a>
                                            )}
                                        </li>

                                        {/* Mobile profile sidebar items */}
                                        {isOpen && isLoggedIn && (
                                            <>
                                                <li className="mobile-profile-divider">
                                                    <span>My Account</span>
                                                </li>
                                                <li className={`mobile-profile-user-info ${isEditingProfile ? 'is-editing' : ''}`}>
                                                    <div className="mobile-user-avatar">
                                                        {isEditingProfile ? (
                                                            <div className="inline-avatar-upload">
                                                                <img src={inlineImagePreview || profileData?.image} alt="Profile" />
                                                                <input type="file" accept="image/*" onChange={handleInlineImageChange} />
                                                                <div className="upload-overlay"><i className="fa fa-camera"></i></div>
                                                            </div>
                                                        ) : (
                                                            profileData && profileData.image && !profileData.image.includes('default/profile') ? (
                                                                <img src={profileData.image} alt="Profile" className="mobile-user-avatar-img" />
                                                            ) : (
                                                                <User size={20} />
                                                            )
                                                        )}
                                                    </div>

                                                    {isEditingProfile ? (
                                                        <div className="mobile-user-edit-form">
                                                            <input
                                                                type="text"
                                                                value={editName}
                                                                onChange={(e) => setEditName(e.target.value)}
                                                                placeholder="Name"
                                                                className="inline-edit-input"
                                                            />
                                                            <input
                                                                type="tel"
                                                                value={editPhone}
                                                                onChange={(e) => setEditPhone(e.target.value)}
                                                                placeholder="Phone"
                                                                className="inline-edit-input"
                                                            />
                                                            <input
                                                                type="email"
                                                                value={editEmail}
                                                                onChange={(e) => setEditEmail(e.target.value)}
                                                                placeholder="Email"
                                                                className="inline-edit-input"
                                                            />
                                                            {profileError && <span className="inline-error">{profileError}</span>}
                                                            <div className="inline-edit-actions">
                                                                <button className="inline-save-btn" onClick={handleSaveProfileInline} disabled={profileSaving}>
                                                                    {profileSaving ? 'Saving' : 'Save'}
                                                                </button>
                                                                <button className="inline-cancel-btn" onClick={handleCancelEditProfile} disabled={profileSaving}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="mobile-user-details">
                                                                <span className="mobile-user-welcome">WELCOME</span>
                                                                <span className="mobile-user-name">{profileData?.name || 'User'}</span>
                                                            </div>
                                                            <button className="mobile-edit-btn" onClick={handleStartEditProfile} aria-label="Edit profile">
                                                                <UserRoundCog size={14} />
                                                            </button>
                                                        </>
                                                    )}
                                                </li>

                                                {/* ✅ Loyalty strip (mobile) — same data/guard as the desktop
                                                    version above; renders nothing when loyalty isn't in the API response. */}
                                                {profileData?.loyalty && (
                                                    <li className="mobile-loyalty-item">
                                                        <button className="dropdown-loyalty-strip" onClick={() => handleSectionClick('loyalty')}>
                                                            <div className="dropdown-loyalty-top">
                                                                <div className="dropdown-loyalty-info">
                                                                    <span className="dropdown-loyalty-badge">
                                                                        <Award size={14} /> {profileData.loyalty.tier?.display || 'Bronze'}
                                                                    </span>
                                                                    <span className="dropdown-loyalty-points">
                                                                        {profileData.loyalty.points?.available ?? 0} pts
                                                                    </span>
                                                                </div>
                                                                <ChevronRight size={16} className="dropdown-loyalty-arrow" />
                                                            </div>
                                                            {typeof profileData.loyalty.tier?.progress?.progress_percentage === 'number' && (
                                                                <div className="dropdown-loyalty-progress-track">
                                                                    <div
                                                                        className="dropdown-loyalty-progress-fill"
                                                                        style={{ width: `${Math.min(100, Math.max(0, profileData.loyalty.tier.progress.progress_percentage))}%` }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </button>
                                                    </li>
                                                )}

                                                <li className="mobile-profile-item">
                                                    <button onClick={() => handleSectionClick('orders')}>
                                                        <ShoppingBag size={16} /> My Orders
                                                    </button>
                                                </li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={() => handleSectionClick('transactions')}>
                                                        <CreditCard size={16} /> My Transactions
                                                    </button>
                                                </li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={() => handleSectionClick('addresses')}>
                                                        <MapPin size={16} /> Addresses
                                                    </button>
                                                </li>
                                                {authMethod === 'email_password' && (
                                                    <li className="mobile-profile-item">
                                                        <button onClick={() => {
                                                            setShowChangePasswordModal(true);
                                                            setIsOpen(false);
                                                            document.body.classList.remove('mobile-menu-open');
                                                        }}>
                                                            <Lock size={16} /> Change Password
                                                        </button>
                                                    </li>
                                                )}
                                                <li className="mobile-profile-item" style={{ display: 'none' }}>
                                                    <button onClick={() => handleSectionClick('preferences')}>
                                                        <Settings size={16} /> Preferences
                                                    </button>
                                                </li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={handleDownloadMyData}>
                                                        <Download size={16} /> Download My Data
                                                    </button>
                                                </li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={handleRequestAccountDeletion}>
                                                        <UserX size={16} /> Delete My Account
                                                    </button>
                                                </li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={handleLogoutClick} className="logout-btn">
                                                        <LogOut size={16} /> Logout
                                                    </button>
                                                </li>
                                            </>
                                        )}
                                        {isOpen && !isLoggedIn && (
                                            <>
                                                <li className="mobile-profile-divider"><span>Account</span></li>
                                                <li className="mobile-profile-item">
                                                    <button onClick={handleLoginClick}>
                                                        <User size={16} /> Login
                                                    </button>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>

                            </nav>
                        </div>
                    </div>
                </header>

                {/* Drawer overlay – click to close */}
                {isOpen && (
                    <div
                        style={{
                            position: 'fixed', inset: 0,
                            background: 'rgba(0,0,0,0.65)',
                            zIndex: 8900,
                        }}
                        onClick={toggleMenu}
                        aria-hidden="true"
                    />
                )}
            </div>

            {showStoreSelector && (
                <StoreSelector
                    isOpen={showStoreSelector}
                    onClose={() => setShowStoreSelector(false)}
                    onSelectStore={handleStoreSelect}
                    userLocation={userLocation}
                    onRequestLocation={handleRequestLocationFromStore}
                />
            )}

            {showLocationModal && (
                <LocationModal
                    isOpen={showLocationModal}
                    onClose={() => setShowLocationModal(false)}
                    onLocationUpdate={handleLocationUpdate}
                    currentLocation={locationDisplay}
                    addresses={propAddresses}
                    selectedAddress={propSelectedAddress}
                    onAddressSelect={handleAddressSelectFromLocation}
                    isLoggedIn={isLoggedIn}
                    onOpenAddressForm={handleOpenAddressForm}
                    userLocation={userLocation}
                />
            )}

            {showUserProfile && (
                <UserProfileModal
                    isOpen={showUserProfile}
                    onClose={() => setShowUserProfile(false)}
                    userPhone={userPhone}
                    userId={userId}
                    isLoggedIn={isLoggedIn}
                    onLoginClick={handleLoginFromProfile}
                    onLogout={handleLogoutClick}
                    onProfileUpdated={fetchProfileData}
                    authMethod={authMethod}
                />
            )}

            {showCartSelectionModal && (
                <CartSelectionModal
                    isOpen={showCartSelectionModal}
                    onClose={() => setShowCartSelectionModal(false)}
                    carts={activeCarts}
                    onSelectCart={handleSelectCart}
                />
            )}

            {/* ✅ Store Closed Modal — shown as an overlay on the current page
                (no navigation), so the Home Page stays mounted behind it. */}
            {showStoreClosedModal && closedStoreInfo && (
                <StoreClosedModal
                    isOpen={showStoreClosedModal}
                    onClose={() => setShowStoreClosedModal(false)}
                    onPreOrder={() => {}}
                    store={closedStoreInfo.store}
                    storeStatus={{ message: closedStoreInfo.message, nextOpenTime: closedStoreInfo.nextOpenTime }}
                    supportsPreOrder={false}
                    hasOtherStores={closedModalHasOtherStores}
                />
            )}

            <ChangePasswordModal
                isOpen={showChangePasswordModal}
                onClose={() => setShowChangePasswordModal(false)}
            />
        </>
    );
};

export default Header;